import * as z from "zod/v4";
import { addSeconds } from "date-fns/addSeconds";
import { utteranceEventMetadataSchema } from "../types";
import { participantSchema } from "./participantSchema";
import type { ReactiveMeetingState, Utterance } from "../meeting";
import { normalizedDifferenceDuration } from "@/lib/utils/duration";
import { invariant } from "@/lib/invariant";

export const utteranceTranscriptionWordSchema = z.object({
  word: z.string(),
  start: z.number(),
  end: z.number(),
  confidence: z.number().min(0).max(1).nullable(),
});
export const piiEntitySchema = z.object({
  start: z.number().int(),
  end: z.number().int(),
  score: z.number().min(0).max(1),
  entity_text: z.string(),
  entity_type: z.string(),
});
const piiDetectionSuccessSchema = z.object({
  result_type: z.literal("success"),
  result_message: z.string(),
  entities: z.array(piiEntitySchema),
});
export const piiDetectionResultSchema = z.discriminatedUnion("result_type", [
  piiDetectionSuccessSchema,
  z.object({
    result_type: z.enum(["skipped", "failed"]),
    result_message: z.string(),
  }),
]);
export type PiiResult = z.infer<typeof piiDetectionResultSchema>;

export const aiContentDetectionSentenceDataSchema = z.object({
  index: z.number().int(),
  label: z.enum(["HUMAN", "AI"]),
  score: z.number().min(0).max(1100),
  sentence: z.string(),
  /** seconds since utterance start time (in float) */
  start: z.number().int(),
  /** seconds since utterance start time (in float) */
  end: z.number().int(),
  start_time: z.number(),
  end_time: z.number(),
  token_count: z.number().int(),
});
export type AiDetectionSentenceData = z.infer<
  typeof aiContentDetectionSentenceDataSchema
>;

const aiContentDetectionSuccessSchema = z.object({
  result_type: z.literal("success"),
  result_message: z.string(),
  overall_score: z.record(z.enum(["AI", "HUMAN"]), z.number().min(0).max(1)),
  sentence_data: z.array(aiContentDetectionSentenceDataSchema),
});
export const aiContentDetectionResultSchema = z.discriminatedUnion(
  "result_type",
  [
    aiContentDetectionSuccessSchema,
    z.object({
      result_type: z.enum(["skipped", "failed"]),
      result_message: z.string(),
    }),
  ],
);
export type AiDetectionResult = z.infer<typeof aiContentDetectionResultSchema>;

export const aiContentDetectionDataSchema =
  aiContentDetectionSuccessSchema.pick({
    overall_score: true,
    sentence_data: true,
  });

export const utteranceTranscriptionListSchema = z.array(
  utteranceTranscriptionWordSchema,
);
export type UtteranceTranscriptionList = z.infer<
  typeof utteranceTranscriptionListSchema
>;

export const transcriptionEventDataSchema = z.object({
  transcription_text: z.string(),
  transcription_words: utteranceTranscriptionListSchema,
  participant: participantSchema,
  error_message: z.string().nullable(),
  ai_results: z
    .object({
      ai_content_detection: aiContentDetectionResultSchema,
      pii: piiDetectionResultSchema,
    })
    .partial(),
});

export const transcriptionEventSchema = z.object({
  event_type: z.literal("bot.utterance.transcription_processed"),
  metadata: utteranceEventMetadataSchema,
  data: transcriptionEventDataSchema,
});

export type TranscriptionEvent = z.infer<typeof transcriptionEventSchema>;

export function transcriptionEventReducer(
  ctx: ReactiveMeetingState,
  event: TranscriptionEvent,
): ReactiveMeetingState {
  const utterance = ctx._utterances.get(event.metadata.bot_utterance_id);
  invariant(
    !!utterance,
    "Utterance should have been created before calling this reducer",
  );

  if (event.data.error_message !== null) {
    const status = {
      status: "error",
      data: null,
      error: event.data.error_message,
    } as const;
    utterance.pii = status;
    utterance.ai_detection = status;
    utterance.transcriptionWords = status;
  } else {
    const { ai_results } = event.data;
    utterance.pii = extractPiiResults(ai_results.pii);
    utterance.ai_detection = extractAiDetectionResults(
      ai_results.ai_content_detection,
    );

    utterance.transcriptionWords = extractTranscriptionWords(
      event.data.transcription_words,
      ctx.recordingStart,
      new Date(event.metadata.bot_utterance_start_datetimetz),
    );

    utterance.text = event.data.transcription_text;
  }

  ctx._utterances.set(event.metadata.bot_utterance_id, utterance);

  return ctx;
}

export function extractTranscriptionWords(
  transcription: UtteranceTranscriptionList,
  recordingStart: Date,
  utteranceStart: Date,
): Utterance["transcriptionWords"] {
  return {
    status: "success",
    data: transcription.map((transcription) => ({
      word: transcription.word,
      start: normalizedDifferenceDuration(
        recordingStart,
        addSeconds(utteranceStart, transcription.start),
      ),
      end: normalizedDifferenceDuration(
        recordingStart,
        addSeconds(utteranceStart, transcription.end),
      ),
    })),
    error: null,
  };
}

export function extractAiDetectionResults(
  aiDetection: AiDetectionResult | undefined,
): Utterance["ai_detection"] {
  if (!aiDetection) {
    return {
      status: "null",
      data: null,
      error: null,
    };
  } else
    switch (aiDetection.result_type) {
      case "success":
        return {
          status: "success",
          data: aiDetection,
          error: null,
        };
      case "skipped":
        return {
          status: "skipped",
          data: null,
          error: null,
        };
      case "failed":
        return {
          status: "error",
          error: aiDetection.result_message,
          data: null,
        };
      default:
        return {
          status: "error",
          error: "unrecognized ai_content_detection result status",
          data: null,
        };
    }
}

export function extractPiiResults(
  pii: PiiResult | undefined,
): Utterance["pii"] {
  if (!pii) {
    return {
      status: "null",
      data: null,
      error: null,
    };
  } else
    switch (pii.result_type) {
      case "success":
        return {
          status: "success",
          data: pii.entities.toSorted((a, b) => a.start - b.start),
          error: null,
        };
      case "skipped":
        return {
          status: "skipped",
          data: null,
          error: null,
        };
      case "failed":
        return {
          status: "error",
          error: pii.result_message,
          data: null,
        };
      default:
        return {
          status: "error",
          error: "unrecognized pii result status",
          data: null,
        };
    }
}
